import EXTERNAL_SEARCH_CONFIG from '@/app/config/search'
import { ROUTES } from '@/app/lib/constants'
import { loadLocaleFiles } from '@/app/lib/load-locale'
import { generateTypesenseScopedKey } from '@/app/modules/search/lib/keys'
import { getSearchClient } from '@/app/modules/search/lib/search-client'
import {
  buildInitialResults,
  sanitizeForSerialization,
} from '@/app/modules/search/lib/ssr'
import { CategoryPage } from '@/app/modules/search/pages/CategoryPage'
import { AvailableLanguages } from '@/app/modules/shared/providers/LanguageSelectionProvider'
import type {
  ICategoryPageCategoryHit,
  ICategoryPageSubcategoriesCoursesHit,
} from '@/types/search'
import { isBoom } from '@hapi/boom'
import { ObjectNotFound } from 'typesense/lib/Typesense/Errors'

// This is the API key used to generate scoped search keys - it should NOT be exposed to the client
const frontendSearchApiKey = process.env.NEXT_SEARCH_INDEX_FRONTEND_API_KEY

type SearchHit =
  | ICategoryPageCategoryHit
  | ICategoryPageSubcategoriesCoursesHit<AvailableLanguages>

const ITEMS_PER_GROUP = 4
const MAX_GROUPS = 2

export async function getServerSideProps(context) {
  const {
    params: { slug },
    locale,
  } = context

  try {
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

    if (!slugRegex.test(slug)) {
      return {
        notFound: true,
      }
    }

    if (!EXTERNAL_SEARCH_CONFIG.enabled || !frontendSearchApiKey) {
      return {
        redirect: {
          destination: ROUTES.search,
          permanent: false,
          locale,
        },
      }
    }

    const messages = await loadLocaleFiles(locale, [
      'navigation',
      'strings',
      'search-page',
      'error',
    ])

    const seed = Math.round(Math.random() * 100)

    const scopedApiKeys = {
      categories: generateTypesenseScopedKey(
        frontendSearchApiKey,
        JSON.stringify({
          filter_by: `is_active:=true`,
        })
      ),
      subcategories_courses: generateTypesenseScopedKey(
        frontendSearchApiKey,
        JSON.stringify({
          filter_by: `$subcategories(is_active:=true&&$categories(slug:=${slug}&&is_active:=true))&&$courses(published:=true)`,
        })
      ),
    }

    const searchClient = getSearchClient('', {
      collectionSpecificSearchParameters: {
        categories: {
          q: '*',
          query_by: 'slug',
          per_page: 1,
          filter_by: `slug:=${slug}&&is_active:=true`,
          include_fields: 'id,name,slug,description',
          'x-typesense-api-key': scopedApiKeys.categories,
        },
        subcategories_courses: {
          q: '*',
          group_by: 'subcategory_id',
          query_by: 'subcategory_id',
          per_page: MAX_GROUPS,
          group_limit: ITEMS_PER_GROUP,
          include_fields: `$subcategories(id,name,slug) as subcategory,$courses(id,name_${locale},learning_description_${locale},min_price,quality_score,education_provider,instructors,level) as course`,
          exclude_fields: 'id,course_id,subcategory_id',
          sort_by: `$subcategories(name:asc),$courses(quality_score:desc,_rand(${seed}):desc)`,
          'x-typesense-api-key': scopedApiKeys.subcategories_courses,
        },
      },
    })

    const searchRequests = [
      { indexName: 'categories', params: { query: '' } },
      { indexName: 'subcategories_courses', params: { query: '' } },
    ]

    const { results } = await searchClient.search<SearchHit>(searchRequests)

    if (!results || results.length !== 2) {
      return {
        notFound: true,
        props: {
          messages,
          locale,
        },
      }
    }

    const [categoryResult, subcategoriesCoursesResult] = results

    const category =
      'hits' in categoryResult && categoryResult.hits.length > 0
        ? (categoryResult.hits[0] as ICategoryPageCategoryHit)
        : null

    if (!category) {
      return {
        notFound: true,
        props: {
          messages,
          locale,
        },
      }
    }

    const csrSearchConfig = {
      subcategories_courses: {
        q: '*',
        group_by: 'subcategory_id',
        query_by: 'subcategory_id',
        per_page: 1,
        group_limit: 4,
        include_fields: `$subcategories(id,name,slug) as subcategory,$courses(id,name_${locale},learning_description_${locale},min_price,quality_score,education_provider,instructors,level) as course`,
        exclude_fields: 'id,course_id,subcategory_id',
        sort_by: `$subcategories(name:asc),$courses(quality_score:desc,_rand(${seed}):desc)`,
        'x-typesense-api-key': scopedApiKeys.subcategories_courses,
      },
    }

    const initialResults = buildInitialResults(
      subcategoriesCoursesResult,
      'subcategories_courses'
    )

    return {
      props: {
        category: sanitizeForSerialization(category),
        csrSearchConfig,
        initialResults: sanitizeForSerialization(initialResults),
        locale,
        messages,
      },
    }
  } catch (e) {
    console.error(e)

    if (e instanceof ObjectNotFound) {
      return {
        notFound: true,
      }
    }

    if (isBoom(e, 401)) {
      return {
        redirect: {
          destination: ROUTES.logout,
          permanent: false,
          locale,
        },
      }
    } else if (isBoom(e, 403)) {
      return {
        redirect: {
          destination: `/${locale}${ROUTES.accessDenied}`,
          permanent: false,
          locale,
        },
      }
    }

    throw e
  }
}

export default CategoryPage
